/**
 * Vendored public API, adapted from piexif-ts (https://github.com/holwech/piexif-ts),
 * MIT License, Copyright (c) Joachim Holwech.
 *
 * `IExifElement` intentionally types tag values as `any` because a single EXIF IFD dictionary
 * mixes numbers, strings, and rational-number tuples for different tags. Reworking that into a
 * discriminated union across the whole vendored module is out of scope here, so type-safety lint
 * rules are disabled for this file rather than for individual lines.
 */
/* eslint-disable */

import { TagValues } from './constants';
import { ErrorCallback, IExif, IExifElement, Types } from './interfaces';
import * as _utils from './utils';

export { Tags, TagValues } from './constants';
export { Types } from './interfaces';
export type { ErrorCallback, IExif, IExifElement } from './interfaces';

export const remove = (imageBinary: string): string => {
	let isBase64Encoded = false;

	if (imageBinary.startsWith('\xff\xd8')) {
		// pass
	} else if (
		imageBinary.startsWith('data:image/jpeg;base64,') ||
		imageBinary.startsWith('data:image/jpg;base64,')
	) {
		imageBinary = _utils.atob(imageBinary.split(',')[1]);
		isBase64Encoded = true;
	} else {
		throw new Error('Given data is not jpeg.');
	}

	const segments = _utils.splitIntoSegments(imageBinary);
	const newSegments = segments.filter(function (segment: string) {
		return !(segment.startsWith('\xff\xe1') && segment.slice(4, 10) == 'Exif\x00\x00');
	});

	let newBinary = newSegments.join('');

	if (isBase64Encoded) {
		newBinary = `data:image/jpeg;base64,${_utils.btoa(newBinary)}`;
	}

	return newBinary;
};

export const insert = (exifBinary: string, imageBinary: string): string => {
	let isBase64Encoded = false;

	if (!exifBinary.startsWith('\x45\x78\x69\x66\x00\x00')) {
		throw new Error('Given data is not exif.');
	}

	if (imageBinary.startsWith('\xff\xd8')) {
		// pass
	} else if (
		imageBinary.startsWith('data:image/jpeg;base64,') ||
		imageBinary.startsWith('data:image/jpg;base64,')
	) {
		imageBinary = _utils.atob(imageBinary.split(',')[1]);
		isBase64Encoded = true;
	} else {
		throw new Error('Given data is not jpeg.');
	}

	const app1Segment = `\xff\xe1${_utils.pack('>H', [exifBinary.length + 2])}${exifBinary}`;
	const segments = _utils.splitIntoSegments(imageBinary);
	let newBinary = _utils.mergeSegments(segments, app1Segment);

	if (isBase64Encoded) {
		newBinary = `data:image/jpeg;base64,${_utils.btoa(newBinary)}`;
	}

	return newBinary;
};

const resolveExifBinary = (binary: string): string => {
	if (typeof binary !== 'string') {
		throw new TypeError("'load' gots invalid type argument.");
	}
	if (binary.startsWith('\xff\xd8')) {
		return binary;
	}
	if (binary.startsWith('data:image/jpeg;base64,') || binary.startsWith('data:image/jpg;base64,')) {
		return _utils.atob(binary.split(',')[1]);
	}
	if (binary.startsWith('Exif')) {
		return binary.slice(6);
	}

	throw new Error("'load' gots invalid file data.");
};

interface IParsedIfds {
	zerothIfd: IExifElement | null;
	firstIfd: IExifElement | null;
	exifIfd: IExifElement | null;
	interopIfd: IExifElement | null;
	gpsIfd: IExifElement | null;
	thumbnail: string | null;
}

const readIfds = (exifReader: _utils.ExifReader): IParsedIfds => {
	let zerothIfd: IExifElement | null = null;
	let firstIfd: IExifElement | null = null;
	let exifIfd: IExifElement | null = null;
	let interopIfd: IExifElement | null = null;
	let gpsIfd: IExifElement | null = null;
	let thumbnail: string | null = null;
	let firstIfdPointer: string | null = null;
	const pointer = _utils.unpack(`${exifReader.endianMark}L`, exifReader.tiftag!.slice(4, 8))[0];

	if (pointer != null) {
		zerothIfd = exifReader.getIfd(pointer, '0th');
		firstIfdPointer = exifReader.getFirstIfdPointer(pointer, '0th');
	}

	if (zerothIfd !== null && 34665 in zerothIfd) {
		exifIfd = exifReader.getIfd(zerothIfd[34665], 'Exif');
	}
	if (zerothIfd !== null && 34853 in zerothIfd) {
		gpsIfd = exifReader.getIfd(zerothIfd[34853], 'GPS');
	}
	if (exifIfd !== null && 40965 in exifIfd) {
		interopIfd = exifReader.getIfd(exifIfd[40965], 'Interop');
	}

	if (firstIfdPointer !== null && firstIfdPointer !== '\x00\x00\x00\x00') {
		const firstIfdPointerValue = _utils.unpack(`${exifReader.endianMark}L`, firstIfdPointer)[0];

		if (firstIfdPointerValue !== null) {
			firstIfd = exifReader.getIfd(firstIfdPointerValue, '1st');
			if (firstIfd !== null && 513 in firstIfd && 514 in firstIfd) {
				const end = firstIfd[513] + firstIfd[514];

				thumbnail = exifReader.tiftag!.slice(firstIfd[513], end);
			}
		}
	}

	return { zerothIfd, firstIfd, exifIfd, interopIfd, gpsIfd, thumbnail };
};

export const load = (binary: string): IExif => {
	const exifBinary = resolveExifBinary(binary);
	const exifObj: IExif = {};
	const exifReader = new _utils.ExifReader(exifBinary);

	if (exifReader.tiftag === null) {
		return exifObj;
	}

	exifReader.endianMark = exifReader.tiftag.startsWith('\x49\x49') ? '<' : '>';

	const parsed = readIfds(exifReader);

	if (parsed.zerothIfd !== null) {
		exifObj['0th'] = parsed.zerothIfd;
	}
	if (parsed.firstIfd !== null) {
		exifObj['1st'] = parsed.firstIfd;
	}
	if (parsed.exifIfd !== null) {
		exifObj['Exif'] = parsed.exifIfd;
	}
	if (parsed.gpsIfd !== null) {
		exifObj['GPS'] = parsed.gpsIfd;
	}
	if (parsed.interopIfd !== null) {
		exifObj['Interop'] = parsed.interopIfd;
	}
	if (parsed.thumbnail !== null) {
		exifObj['thumbnail'] = parsed.thumbnail;
	}

	return exifObj;
};

const hasTags = (ifd?: IExifElement | null): boolean => !!ifd && Object.keys(ifd).length > 0;

interface IIfdPlan {
	zerothIfd: IExifElement;
	exifIfd: IExifElement;
	interopIfd: IExifElement;
	gpsIfd: IExifElement;
	firstIfd: IExifElement;
	hasExifIfd: boolean;
	hasGpsIfd: boolean;
	hasInteropIfd: boolean;
	hasFirstIfd: boolean;
}

const planExifAndInteropIfd = (
	exifObj: IExif,
	zerothIfd: IExifElement
): Pick<IIfdPlan, 'exifIfd' | 'interopIfd' | 'hasExifIfd' | 'hasInteropIfd'> => {
	let exifIfd: IExifElement = {};
	let interopIfd: IExifElement = {};
	let hasExifIfd = false;
	let hasInteropIfd = false;

	if (hasTags(exifObj['Exif']) || hasTags(exifObj['Interop'])) {
		zerothIfd[34665] = 1;
		hasExifIfd = true;
		exifIfd = exifObj['Exif']!;
		if (hasTags(exifObj['Interop'])) {
			exifIfd[40965] = 1;
			hasInteropIfd = true;
			interopIfd = exifObj['Interop']!;
		} else if (Object.keys(exifIfd).includes(TagValues.ExifIFD.InteroperabilityTag.toString())) {
			delete exifIfd[40965];
		}
	} else if (Object.keys(zerothIfd).includes(TagValues.ImageIFD.ExifTag.toString())) {
		delete zerothIfd[34665];
	}

	return { exifIfd, interopIfd, hasExifIfd, hasInteropIfd };
};

const planGpsIfd = (
	exifObj: IExif,
	zerothIfd: IExifElement
): Pick<IIfdPlan, 'gpsIfd' | 'hasGpsIfd'> => {
	let gpsIfd: IExifElement = {};
	let hasGpsIfd = false;

	if (hasTags(exifObj['GPS'])) {
		zerothIfd[TagValues.ImageIFD.GPSTag] = 1;
		hasGpsIfd = true;
		gpsIfd = exifObj['GPS']!;
	} else if (Object.keys(zerothIfd).includes(TagValues.ImageIFD.GPSTag.toString())) {
		delete zerothIfd[TagValues.ImageIFD.GPSTag];
	}

	return { gpsIfd, hasGpsIfd };
};

const planFirstIfd = (exifObj: IExif): Pick<IIfdPlan, 'firstIfd' | 'hasFirstIfd'> => {
	let firstIfd: IExifElement = {};
	let hasFirstIfd = false;

	if ('1st' in exifObj && exifObj['thumbnail'] != null) {
		hasFirstIfd = true;
		exifObj['1st']![513] = 1;
		exifObj['1st']![514] = 1;
		firstIfd = exifObj['1st']!;
	}

	return { firstIfd, hasFirstIfd };
};

const planIfds = (exifObj: IExif): IIfdPlan => {
	const zerothIfd: IExifElement = exifObj['0th'] ?? {};
	const { exifIfd, interopIfd, hasExifIfd, hasInteropIfd } = planExifAndInteropIfd(
		exifObj,
		zerothIfd
	);
	const { gpsIfd, hasGpsIfd } = planGpsIfd(exifObj, zerothIfd);
	const { firstIfd, hasFirstIfd } = planFirstIfd(exifObj);

	return {
		zerothIfd,
		exifIfd,
		interopIfd,
		gpsIfd,
		firstIfd,
		hasExifIfd,
		hasGpsIfd,
		hasInteropIfd,
		hasFirstIfd,
	};
};

interface IIfdByteSets {
	zerothIfdSet: string[];
	zerothIfdLength: number;
	exifIfdSet?: string[];
	exifIfdLength: number;
	gpsIfdBytes: string;
	gpsIfdLength: number;
	interopIfdBytes: string;
	interopIfdLength: number;
	firstIfdSet?: string[];
	thumbnail: string;
}

const computeIfdByteSets = (exifObj: IExif, plan: IIfdPlan): IIfdByteSets => {
	const zerothIfdSet = _utils.dictToBytes(plan.zerothIfd, '0th', 0);
	const zerothIfdLength =
		zerothIfdSet[0].length +
		Number(plan.hasExifIfd) * 12 +
		Number(plan.hasGpsIfd) * 12 +
		4 +
		zerothIfdSet[1].length;

	let exifIfdSet: string[] | undefined;
	let exifIfdLength = 0;
	let gpsIfdBytes = '';
	let gpsIfdLength = 0;
	let interopIfdBytes = '';
	let interopIfdLength = 0;
	let firstIfdSet: string[] | undefined;
	let thumbnail = '';

	if (plan.hasExifIfd) {
		exifIfdSet = _utils.dictToBytes(plan.exifIfd, 'Exif', zerothIfdLength);
		exifIfdLength = exifIfdSet[0].length + Number(plan.hasInteropIfd) * 12 + exifIfdSet[1].length;
	}
	if (plan.hasGpsIfd) {
		const gpsIfdSet = _utils.dictToBytes(plan.gpsIfd, 'GPS', zerothIfdLength + exifIfdLength);

		gpsIfdBytes = gpsIfdSet.join('');
		gpsIfdLength = gpsIfdBytes.length;
	}
	if (plan.hasInteropIfd) {
		const offset = zerothIfdLength + exifIfdLength + gpsIfdLength;
		const interopIfdSet = _utils.dictToBytes(plan.interopIfd, 'Interop', offset);

		interopIfdBytes = interopIfdSet.join('');
		interopIfdLength = interopIfdBytes.length;
	}
	if (plan.hasFirstIfd) {
		const offset = zerothIfdLength + exifIfdLength + gpsIfdLength + interopIfdLength;

		firstIfdSet = _utils.dictToBytes(plan.firstIfd, '1st', offset);
		thumbnail = _utils.getThumbnail(exifObj['thumbnail']!);
		if (thumbnail.length > 64000) {
			throw new Error('Given thumbnail is too large. max 64kB');
		}
	}

	return {
		zerothIfdSet,
		zerothIfdLength,
		exifIfdSet,
		exifIfdLength,
		gpsIfdBytes,
		gpsIfdLength,
		interopIfdBytes,
		interopIfdLength,
		firstIfdSet,
		thumbnail,
	};
};

const buildIfdPointerTag = (key: number, pointerValue: number): string => {
	const pointerBinary = _utils.pack('>L', [pointerValue]);
	const keyBinary = _utils.pack('>H', [key]);
	const typeBinary = _utils.pack('>H', [Types['Long']]);
	const lengthBinary = _utils.pack('>L', [1]);

	return keyBinary + typeBinary + lengthBinary + pointerBinary;
};

const buildFirstIfdBytes = (
	firstIfdSet: string[],
	pointerValue: number,
	thumbnail: string
): string => {
	const thumbnailPointer = pointerValue + firstIfdSet[0].length + 24 + 4 + firstIfdSet[1].length;
	const thumbnailPointerBinary = `\x02\x01\x00\x04\x00\x00\x00\x01${_utils.pack('>L', [thumbnailPointer])}`;
	const thumbnailLengthBinary = `\x02\x02\x00\x04\x00\x00\x00\x01${_utils.pack('>L', [thumbnail.length])}`;

	return `${firstIfdSet[0]}${thumbnailPointerBinary}${thumbnailLengthBinary}\x00\x00\x00\x00${firstIfdSet[1]}${thumbnail}`;
};

export const dump = (originalExifObj: IExif): string => {
	const TIFF_HEADER_LENGTH = 8;
	const header = 'Exif\x00\x00\x4d\x4d\x00\x2a\x00\x00\x00\x08';
	const exifObj: IExif = _utils.copy(originalExifObj);
	const plan = planIfds(exifObj);
	const byteSets = computeIfdByteSets(exifObj, plan);

	let exifPointer = '';
	let gpsPointer = '';
	let interopPointer = '';
	let firstIfdPointer = '\x00\x00\x00\x00';
	let firstIfdBytes = '';

	if (plan.hasExifIfd) {
		exifPointer = buildIfdPointerTag(34665, TIFF_HEADER_LENGTH + byteSets.zerothIfdLength);
	}
	if (plan.hasGpsIfd) {
		gpsPointer = buildIfdPointerTag(
			34853,
			TIFF_HEADER_LENGTH + byteSets.zerothIfdLength + byteSets.exifIfdLength
		);
	}
	if (plan.hasInteropIfd) {
		interopPointer = buildIfdPointerTag(
			40965,
			TIFF_HEADER_LENGTH + byteSets.zerothIfdLength + byteSets.exifIfdLength + byteSets.gpsIfdLength
		);
	}
	if (plan.hasFirstIfd) {
		const pointerValue =
			TIFF_HEADER_LENGTH +
			byteSets.zerothIfdLength +
			byteSets.exifIfdLength +
			byteSets.gpsIfdLength +
			byteSets.interopIfdLength;

		firstIfdPointer = _utils.pack('>L', [pointerValue]);
		firstIfdBytes = buildFirstIfdBytes(byteSets.firstIfdSet!, pointerValue, byteSets.thumbnail);
	}

	const zerothIfdBinary =
		byteSets.zerothIfdSet[0] +
		exifPointer +
		gpsPointer +
		firstIfdPointer +
		byteSets.zerothIfdSet[1];
	let exifIfdBytes = '';

	if (plan.hasExifIfd) {
		exifIfdBytes = byteSets.exifIfdSet![0] + interopPointer + byteSets.exifIfdSet![1];
	}

	return (
		header +
		zerothIfdBinary +
		exifIfdBytes +
		byteSets.gpsIfdBytes +
		byteSets.interopIfdBytes +
		firstIfdBytes
	);
};

export const setErrorByPass = (bypass: boolean, errorCallback?: ErrorCallback): void => {
	_utils.setErrorByPass(bypass, errorCallback);
};

export const resetOrientation = (exifObject: IExif): IExif => {
	const orientationKey = TagValues.ImageIFD.Orientation;

	if (exifObject['0th']) {
		if (exifObject['0th'][orientationKey]) {
			exifObject['0th'][orientationKey] = 1;
		}
	}

	return exifObject;
};
